import { assetUrl } from './assetUrl.js';

const PANEL_ROOT_ID = 'slot-sysmsg-panel';
const TOAST_ROOT_ID = 'slot-sysmsg-toast';

/** @typedef {'connection' | 'session' | 'idle'} SystemMessageIcon */

const SESSION_ENDED_ICON_SVG = `
  <svg class="slot-sysmsg__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <rect x="8" y="12" width="22" height="28" rx="3.5" stroke="#F0D78C" stroke-width="2" />
    <rect x="18" y="8" width="22" height="28" rx="3.5" fill="#2A2018" stroke="#E7C463" stroke-width="2" opacity="0.92" />
    <path d="M24 16h10M24 22h10M24 28h6" stroke="#C9A227" stroke-width="1.75" stroke-linecap="round" />
    <circle cx="14" cy="20" r="1.25" fill="#E7C463" />
    <circle cx="14" cy="26" r="1.25" fill="#E7C463" />
    <path d="M30 8v3.5" stroke="#7A5530" stroke-width="2" stroke-linecap="round" />
  </svg>
`;

const IDLE_TIMEOUT_ICON_SVG = `
  <svg class="slot-sysmsg__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <circle cx="24" cy="26" r="14" stroke="#F0D78C" stroke-width="2" />
    <path d="M24 18v9l6 4" stroke="#E7C463" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M24 8v4M14.5 10.8l2 3.5M33.5 10.8l-2 3.5" stroke="#C9A227" stroke-width="1.75" stroke-linecap="round" />
    <circle cx="24" cy="26" r="1.5" fill="#E7C463" />
  </svg>
`;

const TOAST_SUCCESS_ICON_SVG = `
  <svg class="slot-sysmsg-toast__icon-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="8.5" stroke="#E7C463" stroke-width="1.5" />
    <path d="M6.5 10.2 8.8 12.5 13.8 7.5" stroke="#F5E6A8" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
  </svg>
`;

/**
 * @param {SystemMessageIcon} kind
 */
function iconMarkupForKind(kind) {
  if (kind === 'session') return SESSION_ENDED_ICON_SVG;
  if (kind === 'idle') return IDLE_TIMEOUT_ICON_SVG;
  return `<img class="slot-sysmsg__icon slot-sysmsg__icon--img" src="${assetUrl('/assets/connection_lost.svg')}" alt="" decoding="async" draggable="false" />`;
}

/**
 * Egypt-themed blocking system message (connection lost, session ended, idle timeout).
 * @param {{
 *   mountEl?: HTMLElement | null,
 *   onPrimaryClick?: () => void,
 * }} [opts]
 */
export function createSystemMessagePanel({ mountEl = typeof document !== 'undefined' ? document.body : null, onPrimaryClick } = {}) {
  if (!mountEl || typeof document === 'undefined') {
    return {
      show: () => {},
      hide: () => {},
      isVisible: () => false,
      update: () => {},
      dispose: () => {},
      el: null,
    };
  }

  let root = document.getElementById(PANEL_ROOT_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = PANEL_ROOT_ID;
    root.className = 'slot-sysmsg';
    root.setAttribute('role', 'alertdialog');
    root.setAttribute('aria-modal', 'true');
    root.innerHTML = `
      <div class="slot-sysmsg__backdrop" aria-hidden="true"></div>
      <div class="slot-sysmsg__content">
        <div class="slot-sysmsg__panel" role="presentation">
          <div class="slot-sysmsg__panel-rim" aria-hidden="true"></div>
          <div class="slot-sysmsg__panel-accent slot-sysmsg__panel-accent--top" aria-hidden="true"></div>
          <div class="slot-sysmsg__panel-accent slot-sysmsg__panel-accent--bottom" aria-hidden="true"></div>
          <div class="slot-sysmsg__panel-inner">
            <img
              class="slot-sysmsg__logo"
              src="${assetUrl('/assets/Logo0.png')}"
              alt=""
              decoding="async"
              draggable="false"
            />
            <div class="slot-sysmsg__icon-badge" aria-hidden="true"></div>
            <h1 class="slot-sysmsg__title"></h1>
            <div class="slot-sysmsg__divider" aria-hidden="true"></div>
            <p class="slot-sysmsg__body"></p>
            <p class="slot-sysmsg__status" aria-live="polite"></p>
            <button type="button" class="slot-sysmsg__btn slot-sysmsg__btn--primary"></button>
          </div>
        </div>
      </div>
    `;
    mountEl.appendChild(root);
  }

  const titleEl = root.querySelector('.slot-sysmsg__title');
  const bodyEl = root.querySelector('.slot-sysmsg__body');
  const statusEl = root.querySelector('.slot-sysmsg__status');
  const iconBadgeEl = root.querySelector('.slot-sysmsg__icon-badge');
  const primaryBtn = root.querySelector('.slot-sysmsg__btn--primary');
  const logoEl = root.querySelector('.slot-sysmsg__logo');

  /** @type {SystemMessageIcon} */
  let iconKind = 'connection';
  let visible = false;

  const applyIcon = () => {
    if (!iconBadgeEl) return;
    iconBadgeEl.innerHTML = iconMarkupForKind(iconKind);
    iconBadgeEl.classList.toggle('slot-sysmsg__icon-badge--pulse', iconKind === 'connection');
  };

  const onBtnClick = () => {
    onPrimaryClick?.();
  };
  primaryBtn?.addEventListener('click', onBtnClick);

  applyIcon();

  return {
    el: root,
    show() {
      visible = true;
      root.classList.add('slot-sysmsg--visible');
      root.toggleAttribute('inert', false);
      window.getSelection?.()?.removeAllRanges?.();
      titleEl?.blur?.();
      bodyEl?.blur?.();
      if (primaryBtn && !primaryBtn.classList.contains('slot-sysmsg__btn--hidden')) {
        primaryBtn.focus({ preventScroll: true });
      }
    },
    hide() {
      visible = false;
      root.classList.remove('slot-sysmsg--visible');
      root.toggleAttribute('inert', true);
    },
    isVisible: () => visible,
    /**
     * @param {{
     *   title?: string,
     *   body?: string,
     *   icon?: SystemMessageIcon,
     *   statusText?: string,
     *   statusVisible?: boolean,
     *   primaryLabel?: string,
     *   showPrimaryButton?: boolean,
     *   showLogo?: boolean,
     * }} patch
     */
    update(patch = {}) {
      if (patch.icon != null) {
        iconKind = patch.icon;
        applyIcon();
      }
      if (patch.title != null && titleEl) titleEl.textContent = patch.title;
      if (patch.body != null && bodyEl) bodyEl.textContent = patch.body;
      if (patch.statusText != null && statusEl) statusEl.textContent = patch.statusText;
      if (patch.statusVisible != null && statusEl) {
        statusEl.classList.toggle('slot-sysmsg__status--visible', !!patch.statusVisible && !!statusEl.textContent);
      } else if (patch.statusText != null && statusEl) {
        statusEl.classList.toggle('slot-sysmsg__status--visible', !!patch.statusText);
      }
      if (patch.primaryLabel != null && primaryBtn) primaryBtn.textContent = patch.primaryLabel;
      if (patch.showPrimaryButton != null && primaryBtn) {
        primaryBtn.classList.toggle('slot-sysmsg__btn--hidden', !patch.showPrimaryButton);
      }
      if (patch.showLogo != null && logoEl) {
        logoEl.classList.toggle('slot-sysmsg__logo--hidden', !patch.showLogo);
      }
      const label = [titleEl?.textContent, bodyEl?.textContent].filter(Boolean).join('. ');
      if (label) root.setAttribute('aria-label', label);
    },
    dispose() {
      primaryBtn?.removeEventListener('click', onBtnClick);
      root?.remove();
    },
  };
}

/**
 * Egypt-themed transient status toast (e.g. connection restored).
 * @param {{
 *   mountEl?: HTMLElement | null,
 *   message: string,
 *   durationMs?: number,
 * }} opts
 * @returns {{ hide: () => void }}
 */
export function showSystemToast({ mountEl = typeof document !== 'undefined' ? document.body : null, message, durationMs = 3500 }) {
  if (!mountEl || typeof document === 'undefined') {
    return { hide: () => {} };
  }

  let root = document.getElementById(TOAST_ROOT_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = TOAST_ROOT_ID;
    root.className = 'slot-sysmsg-toast';
    root.setAttribute('role', 'status');
    root.innerHTML = `
      <span class="slot-sysmsg-toast__icon" aria-hidden="true">${TOAST_SUCCESS_ICON_SVG}</span>
      <span class="slot-sysmsg-toast__text"></span>
    `;
    mountEl.appendChild(root);
  }

  const textEl = root.querySelector('.slot-sysmsg-toast__text');
  if (textEl) textEl.textContent = message;
  root.classList.add('slot-sysmsg-toast--visible');

  /** @type {ReturnType<typeof setTimeout> | null} */
  let timer = null;

  const hide = () => {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
    root?.classList.remove('slot-sysmsg-toast--visible');
  };

  timer = setTimeout(hide, durationMs);
  return { hide };
}
